import { AlertCircle, Info } from "lucide-react";
import { MessageGeneration } from "../types/chat";
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { InfoCircle } from 'iconoir-react';
import {
    CHAT_MESSAGE_ACTION_ICON_COMPACT_PROPS,
    CHAT_MESSAGE_ACTION_ICON_PROPS,
} from 'constant/chat-layout';
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { getPipelineResponseAnalytics } from "../services/feedback-service";
import { useConfig } from "hooks/use-config";
import { useChatStore } from "../../../state/store/chat/use-chat-store";
import { useTranslation } from "react-i18next";
import { useModelStore } from "../../../state/store/chat/use-model-store";

interface PipelineAnalyticsProps {
    message: MessageGeneration;
    conversationId: string;
    recordId: string;
    /** Fallback label when message metadata/usedModelId is missing. */
    agentModelLabel?: string | null;
}

enum AnalyticsErrorType {
    NOT_FOUND = "NOT_FOUND",
    UNKNOWN = "UNKNOWN"
}

interface AnalyticsError {
    type: AnalyticsErrorType;
    message: string;
}

function parseAnalyticsError(error: any): AnalyticsError {

    const message = error?.['error']?.['detail'] ?? "Unexpected analytics error";

    if (error && typeof (error) == 'object' && 'status' in error) {
        const status = error['status'];

        switch (status) {
            case 404:
                return {
                    type: AnalyticsErrorType.NOT_FOUND,
                    message
                }
            default:
                return {
                    type: AnalyticsErrorType.UNKNOWN,
                    message
                }
        }
    }

    return {
        type: AnalyticsErrorType.UNKNOWN,
        message
    }
}

export function PipelineAnalytics({
    message,
    conversationId,
    recordId,
    agentModelLabel,
}: PipelineAnalyticsProps) {
    const updateMessageAnalytics = useChatStore((state) => state.updateMessageAnalytics);
    const models = useModelStore((state) => state.models);
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<AnalyticsError | null>(null);
    const [hasAttemptedFetch, setHasAttemptedFetch] = useState(!!message.responseAnalytics);

    const analytics = message.responseAnalytics;
    const rawModelId =
        message.usedModelId ||
        message.metadata?.selected_model ||
        message.metadata?.selectedModel ||
        message.metadata?.autoSelectedModel ||
        message.metadata?.model ||
        message.metadata?.model_name ||
        message.metadata?.modelName ||
        message.metadata?.model_id ||
        message.metadata?.modelId ||
        message.metadata?.agentRun?.model ||
        message.metadata?.agentRun?.Model;

    const usedModel = rawModelId
        ? models.find(
            (model) =>
                model.id === rawModelId ||
                model.name === rawModelId ||
                (model.id && rawModelId && model.id.toLowerCase() === rawModelId.toLowerCase()) ||
                (model.name && rawModelId && model.name.toLowerCase() === rawModelId.toLowerCase())
        )
        : null;

    const isUuid = (str?: string | null) =>
        Boolean(str && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str));

    // Never lock to agentModelLabel: user can change selected model even when an agent is selected.
    // Prioritize the actual model used from the message/metadata.
    const modelUsedLabel =
        usedModel?.name ||
        (rawModelId && !isUuid(rawModelId) ? rawModelId : null) ||
        agentModelLabel ||
        null;

    const { configurationSettings } = useConfig();

    const fetchAnalytics = async () => {

        if (analytics || isLoading || hasAttemptedFetch) return;

        setIsLoading(true);
        try {
            const response = await getPipelineResponseAnalytics({
                conversationId,
                messageId: message.id
            }, configurationSettings);
            updateMessageAnalytics(recordId, message.id, response);
        } catch (error) {
            setError(parseAnalyticsError(error));
        } finally {
            setIsLoading(false);
            setHasAttemptedFetch(true);
        }
    }

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        await fetchAnalytics();
    }

    useEffect(() => {
        if (conversationId && message.id && !analytics && isOpen && !hasAttemptedFetch) {
            fetchAnalytics();
        }
    }, [isOpen]);

    useEffect(() => {
        setHasAttemptedFetch(false);
    }, [conversationId, message.id])


    const renderTooltipContent = () => {
        if (isLoading) {
            return (
                <div className="flex items-center gap-2 py-1">
                    <Loader2 size={14} className="animate-spin" />
                    <span className="text-xs">{t('LOADING_ANALYTICS')}...</span>
                </div>
            );
        }

        if (error) {

            if (error.type === AnalyticsErrorType.NOT_FOUND) {
                return (
                    <div className="text-xs py-1">
                        {t('NO_ANALYTICS_AVAILABLE')}
                    </div>
                );
            }

            return (
                <div className="flex flex-col gap-2 py-1 min-w-[200px]">
                    <div className="flex items-start gap-2">
                        <AlertCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
                        <div className="flex flex-col gap-1 flex-1">
                            <span className="text-xs font-medium">{t('ERROR_LOADING_ANALYTICS')}</span>
                        </div>
                    </div>
                </div>
            );
        }

        if (analytics || modelUsedLabel) {
            return (
                <div className="py-1 space-y-2 min-w-[220px]">
                    <div className="text-xs font-semibold border-b border-primary-foreground/20 pb-1.5">
                        {t('RESPONSE_ANALYTICS')}
                    </div>
                    <div className="space-y-1.5 text-xs">
                        {analytics?.timeToFirstTokenMs !== undefined && (
                            <div className="flex justify-between gap-4">
                                <span className="opacity-90">{t('TIME_TO_FIRST_TOKEN')}:</span>
                                <span className="font-medium">{(analytics.timeToFirstTokenMs / 1000).toFixed(2)}s</span>
                            </div>
                        )}
                        {analytics?.totalPipelineDurationMs && (
                            <div className="flex justify-between gap-4">
                                <span className="opacity-90">{t('TOTAL_PIPELINE_DURATION')}:</span>
                                <span className="font-medium">{(analytics.totalPipelineDurationMs / 1000).toFixed(2)}s</span>
                            </div>
                        )}
                        {modelUsedLabel && (
                            <div className="flex justify-between gap-4">
                                <span className="opacity-90">{t('MODEL_USED')}:</span>
                                <span className="font-medium">{modelUsedLabel}</span>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return (
            <div className="text-xs py-1">
                {t('NO_ANALYTICS_AVAILABLE')}
            </div>
        );
    }

    return (
        <TooltipProvider delayDuration={300}>
            <Tooltip open={isOpen} onOpenChange={setIsOpen}>
                <TooltipTrigger asChild>
                    <div className="relative inline-block">
                        <button
                            className="inline-flex shrink-0 size-8 items-center justify-center rounded-full p-0 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleClick}
                        >
                            <InfoCircle {...CHAT_MESSAGE_ACTION_ICON_COMPACT_PROPS} />
                        </button>
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    {renderTooltipContent()}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}